import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getStale, setCached } from '../lib/pageCache';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { 
  TrendingUp,
  TrendingDown,
  Users,
  Briefcase,
  Clock,
  Target,
  CheckCircle2,
  AlertCircle,
  BarChart3,
  PieChart,
  Activity,
  Calendar,
  ArrowRight,
  Loader2,
  Lock,
  Sparkles
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend,
  AreaChart,
  Area
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Color palette
const COLORS = ['#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ef4444', '#6366f1', '#ec4899'];
const STATUS_COLORS = {
  'Completed': '#10b981',
  'In Progress': '#f59e0b',
  'Scheduled': '#3b82f6',
  'Pending': '#fbbf24',
  'Cancelled': '#94a3b8'
};

export default function Reports() {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');

  const canViewReports = hasPermission('view_reports');

  const fetchAnalytics = useCallback(async () => {
    if (!canViewReports) return;
    const cacheKey = `reports-analytics:${period}`;
    const cached = getStale(cacheKey);
    if (cached) {
      setAnalytics(cached);
      setLoading(false);
    } else {
      setLoading(true);
    }
    try {
      const response = await axios.get(`${API}/reports/analytics?period=${period}`);
      setAnalytics(response.data);
      setCached(cacheKey, response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  }, [period, canViewReports]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  // Show access denied if no permission
  if (!canViewReports) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-center">
        <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
          <Lock className="w-8 h-8 text-slate-400" />
        </div>
        <h2 className="text-xl font-bold text-slate-900 mb-2">Access Restricted</h2>
        <p className="text-slate-500 max-w-md">
          You don&apos;t have permission to view reports. Please contact your administrator to request access.
        </p>
        <Button onClick={() => navigate('/')} className="mt-6 bg-amber-500 hover:bg-amber-600">
          Go to Dashboard
        </Button>
      </div>
    );
  }

  if (loading && !analytics) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  const { pipeline, job_performance, team_performance, efficiency } = analytics || {};

  // Prepare chart data
  const pipelineData = [
    { name: 'Opportunities', value: pipeline?.opportunities?.total || 0, fill: '#3b82f6' },
    { name: 'Proposals', value: pipeline?.proposals?.total || 0, fill: '#8b5cf6' },
    { name: 'Accepted', value: pipeline?.proposals?.accepted || 0, fill: '#10b981' }
  ];

  const statusData = Object.entries(job_performance?.by_status || {}).map(([name, value]) => ({
    name,
    value,
    fill: STATUS_COLORS[name] || '#94a3b8'
  }));

  const serviceTypeData = Object.entries(job_performance?.by_service_type || {}).map(([name, value], idx) => ({
    name: name.length > 15 ? name.substring(0, 15) + '...' : name,
    fullName: name,
    value,
    fill: COLORS[idx % COLORS.length]
  }));

  const teamData = (team_performance?.members || []).slice(0, 8).map((m, idx) => ({
    name: m.name.split(' ')[0],
    fullName: m.name,
    jobs: m.jobs_assigned,
    hours: m.hours_logged,
    fill: COLORS[idx % COLORS.length]
  }));

  const jobsTrendData = job_performance?.jobs_over_time || [];

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Barlow_Condensed'] tracking-tight">
            Analytics Dashboard
          </h1>
          <p className="text-slate-500 mt-1">Business performance insights</p>
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-40" data-testid="analytics-period">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="week">Last 7 Days</SelectItem>
            <SelectItem value="month">Last 30 Days</SelectItem>
            <SelectItem value="quarter">Last 90 Days</SelectItem>
            <SelectItem value="year">Last Year</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Section 1: Sales Pipeline */}
      <div className="space-y-4">
        <h2 className="text-xl font-bold text-slate-800 font-['Barlow_Condensed'] flex items-center gap-2">
          <Target className="w-5 h-5 text-amber-500" />
          Sales Pipeline
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Conversion Funnel Stats */}
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-blue-600">Opportunities</p>
                  <p className="text-2xl font-bold text-blue-900">{pipeline?.opportunities?.total || 0}</p>
                </div>
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-white" />
                </div>
              </div>
              <p className="text-xs text-blue-600 mt-2">
                ${((pipeline?.opportunities?.total_value || 0) / 1000).toFixed(1)}k pipeline value
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-purple-600">Proposals</p>
                  <p className="text-2xl font-bold text-purple-900">{pipeline?.proposals?.total || 0}</p>
                </div>
                <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                  <ArrowRight className="w-5 h-5 text-white" />
                </div>
              </div>
              <p className="text-xs text-purple-600 mt-2">
                {pipeline?.conversion_rates?.opportunity_to_proposal || 0}% from opportunities
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-emerald-600">Accepted</p>
                  <p className="text-2xl font-bold text-emerald-900">{pipeline?.proposals?.accepted || 0}</p>
                </div>
                <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-white" />
                </div>
              </div>
              <p className="text-xs text-emerald-600 mt-2">
                {pipeline?.conversion_rates?.proposal_to_job || 0}% win rate
              </p>
            </CardContent>
          </Card>

          {/* Avg Conversion Time tile */}
          {(() => {
            const ct = pipeline?.conversion_time || {};
            const days = ct.avg_days_current;
            const prior = ct.avg_days_prior;
            const trend = ct.trend || 'flat';
            const pct = ct.pct_change;
            const trendColor =
              trend === 'shorter' ? 'text-emerald-700'
              : trend === 'longer' ? 'text-red-700'
              : 'text-amber-700';
            const trendBg =
              trend === 'shorter' ? 'from-emerald-50 to-emerald-100 border-emerald-200'
              : trend === 'longer' ? 'from-red-50 to-red-100 border-red-200'
              : 'from-amber-50 to-amber-100 border-amber-200';
            const iconBg =
              trend === 'shorter' ? 'bg-emerald-500'
              : trend === 'longer' ? 'bg-red-500'
              : 'bg-amber-500';
            const headerColor =
              trend === 'shorter' ? 'text-emerald-600'
              : trend === 'longer' ? 'text-red-600'
              : 'text-amber-600';
            const numberColor =
              trend === 'shorter' ? 'text-emerald-900'
              : trend === 'longer' ? 'text-red-900'
              : 'text-amber-900';
            const TrendIcon = trend === 'shorter' ? TrendingDown : trend === 'longer' ? TrendingUp : Activity;
            const subtitle = (() => {
              if (days == null) return 'No accepted proposals in period';
              if (prior == null || pct == null) return `Avg time proposal → job (no prior data)`;
              const direction = trend === 'shorter' ? 'faster' : trend === 'longer' ? 'slower' : 'same as';
              return `${Math.abs(pct)}% ${direction} vs prior period (${prior}d)`;
            })();
            return (
              <Card className={`bg-gradient-to-br border ${trendBg}`} data-testid="conversion-time-tile">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`text-xs font-bold uppercase tracking-wider ${headerColor}`}>Avg Conversion</p>
                      <p className={`text-2xl font-bold ${numberColor}`}>
                        {days != null ? `${days}d` : '—'}
                      </p>
                    </div>
                    <div className={`w-10 h-10 ${iconBg} rounded-lg flex items-center justify-center`}>
                      <TrendIcon className="w-5 h-5 text-white" />
                    </div>
                  </div>
                  <p className={`text-xs ${trendColor} mt-2`}>
                    {subtitle}
                  </p>
                </CardContent>
              </Card>
            );
          })()}

          <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-red-600">Lost</p>
                  <p className="text-2xl font-bold text-red-900">{pipeline?.proposals?.rejected || 0}</p>
                </div>
                <div className="w-10 h-10 bg-red-500 rounded-lg flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-white" />
                </div>
              </div>
              <p className="text-xs text-red-600 mt-2">
                Lost opportunities
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Pipeline Chart */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-['Barlow_Condensed']">Conversion Funnel</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={pipelineData} layout="vertical" margin={{ left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" stroke="#94a3b8" />
                  <YAxis 
                    type="category" 
                    dataKey="name" 
                    stroke="#94a3b8" 
                    width={90} 
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                  />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                    {pipelineData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Lost Reasons breakdown — only rendered when there's at least one
            Lost proposal with data in the selected period. */}
        {(pipeline?.proposals?.lost_reasons?.length > 0) && (
          <Card className="border-slate-200" data-testid="lost-reasons-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-['Barlow_Condensed']">Lost Reasons</CardTitle>
              <p className="text-sm text-slate-500">Why proposals were marked Lost this period</p>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={pipeline.proposals.lost_reasons}
                    layout="vertical"
                    margin={{ left: 10, right: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" allowDecimals={false} stroke="#94a3b8" />
                    <YAxis
                      type="category"
                      dataKey="reason"
                      stroke="#94a3b8"
                      width={140}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                    />
                    <Bar dataKey="count" fill="#ef4444" radius={[0, 4, 4, 0]} name="Lost proposals" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Revenue by Service Type */}
        {(pipeline?.revenue_by_service?.length > 0) && (
          <Card className="border-slate-200" data-testid="revenue-by-service-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-['Barlow_Condensed']">Revenue by Service</CardTitle>
              <p className="text-xs text-slate-500">Total accepted-proposal value per service vs prior period</p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {pipeline.revenue_by_service.map((row) => {
                  const trendColor =
                    row.trend === 'up' ? 'text-emerald-700'
                    : row.trend === 'down' ? 'text-red-700'
                    : row.trend === 'new' ? 'text-blue-700'
                    : 'text-slate-500';
                  const TrendIcon =
                    row.trend === 'up' ? TrendingUp
                    : row.trend === 'down' ? TrendingDown
                    : row.trend === 'new' ? Sparkles
                    : Activity;
                  const subtitle = (() => {
                    if (row.trend === 'new') return 'New this period';
                    if (row.pct_change == null) return 'No prior data';
                    const dir = row.trend === 'up' ? 'up' : row.trend === 'down' ? 'down' : 'flat';
                    return `${dir === 'flat' ? 'Flat' : `${Math.abs(row.pct_change)}% ${dir}`} vs prior ($${(row.prior_value / 1000).toFixed(1)}k)`;
                  })();
                  return (
                    <div
                      key={row.service}
                      className="border border-slate-200 rounded-lg p-3 bg-white hover:border-amber-300 transition-colors"
                      data-testid={`revenue-tile-${row.service.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 truncate" title={row.service}>
                            {row.service}
                          </p>
                          <p className="text-2xl font-bold text-slate-900 mt-1">
                            ${row.current_value >= 1000
                              ? `${(row.current_value / 1000).toFixed(1)}k`
                              : Math.round(row.current_value).toLocaleString()}
                          </p>
                        </div>
                        <TrendIcon className={`w-5 h-5 ${trendColor} flex-shrink-0`} />
                      </div>
                      <p className={`text-xs mt-2 ${trendColor}`}>{subtitle}</p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Section 2: Job Performance */}
      <div className="space-y-4">
        <h2 className="text-xl font-bold text-slate-800 font-['Barlow_Condensed'] flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-amber-500" />
          Job Performance
        </h2>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200">
            <CardContent className="p-4">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-600">Total Jobs</p>
              <p className="text-2xl font-bold text-slate-900">{job_performance?.total || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
            <CardContent className="p-4">
              <p className="text-xs font-bold uppercase tracking-wider text-emerald-600">Completed</p>
              <p className="text-2xl font-bold text-emerald-900">{job_performance?.by_status?.Completed || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
            <CardContent className="p-4">
              <p className="text-xs font-bold uppercase tracking-wider text-amber-600">In Progress</p>
              <p className="text-2xl font-bold text-amber-900">{job_performance?.by_status?.['In Progress'] || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-4">
              <p className="text-xs font-bold uppercase tracking-wider text-blue-600">Scheduled</p>
              <p className="text-2xl font-bold text-blue-900">{job_performance?.by_status?.Scheduled || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="p-4">
              <p className="text-xs font-bold uppercase tracking-wider text-purple-600">Completion Rate</p>
              <p className="text-2xl font-bold text-purple-900">{job_performance?.completion_rate || 0}%</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Jobs Over Time */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-['Barlow_Condensed']">Jobs Over Time</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={jobsTrendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#94a3b8" 
                      tick={{ fontSize: 11 }}
                      tickFormatter={(val) => val.slice(5)}
                    />
                    <YAxis stroke="#94a3b8" />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                    />
                    <Legend />
                    <Area type="monotone" dataKey="scheduled" name="Scheduled" stroke="#3b82f6" fill="#93c5fd" strokeWidth={2} />
                    <Area type="monotone" dataKey="completed" name="Completed" stroke="#10b981" fill="#6ee7b7" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Status Distribution */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-['Barlow_Condensed']">Status Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPieChart>
                    <Pie
                      data={statusData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {statusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend 
                      layout="vertical" 
                      align="right" 
                      verticalAlign="middle"
                      wrapperStyle={{ fontSize: '12px' }}
                    />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Service Type Breakdown */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-['Barlow_Condensed']">Jobs by Service Type</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={serviceTypeData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" stroke="#94a3b8" tick={{ fontSize: 11 }} />
                  <YAxis stroke="#94a3b8" />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                    formatter={(value, name, props) => [value, props.payload.fullName]}
                  />
                  <Bar dataKey="value" name="Jobs" radius={[4, 4, 0, 0]}>
                    {serviceTypeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Section 3: Team Performance */}
      <div className="space-y-4">
        <h2 className="text-xl font-bold text-slate-800 font-['Barlow_Condensed'] flex items-center gap-2">
          <Users className="w-5 h-5 text-amber-500" />
          Team Performance
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Team Members</p>
                  <p className="text-2xl font-bold text-slate-900">{team_performance?.members?.length || 0}</p>
                </div>
                <Users className="w-8 h-8 text-slate-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Total Hours Logged</p>
                  <p className="text-2xl font-bold text-slate-900">{(team_performance?.total_hours || 0).toFixed(1)}</p>
                </div>
                <Clock className="w-8 h-8 text-slate-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Avg Hours/Member</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {team_performance?.members?.length > 0 
                      ? ((team_performance?.total_hours || 0) / team_performance.members.length).toFixed(1)
                      : '0'}
                  </p>
                </div>
                <Activity className="w-8 h-8 text-slate-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Jobs per Team Member */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-['Barlow_Condensed']">Jobs per Team Member</CardTitle>
            </CardHeader>
            <CardContent>
              <div style={{ height: Math.max(256, teamData.length * 28 + 40) }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={teamData} layout="vertical" margin={{ top: 5, right: 10, bottom: 5, left: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" stroke="#94a3b8" />
                    <YAxis type="category" dataKey="name" stroke="#94a3b8" width={80} interval={0} tick={{ fontSize: 12 }} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                      formatter={(value, name, props) => [value, props.payload.fullName]}
                    />
                    <Bar dataKey="jobs" name="Jobs Assigned" fill="#f59e0b" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Hours per Team Member */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-['Barlow_Condensed']">Hours Logged per Member</CardTitle>
            </CardHeader>
            <CardContent>
              <div style={{ height: Math.max(256, teamData.length * 28 + 40) }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={teamData} layout="vertical" margin={{ top: 5, right: 10, bottom: 5, left: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" stroke="#94a3b8" />
                    <YAxis type="category" dataKey="name" stroke="#94a3b8" width={80} interval={0} tick={{ fontSize: 12 }} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                      formatter={(value, name, props) => [`${value}h`, props.payload.fullName]}
                    />
                    <Bar dataKey="hours" name="Hours" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Team Leaderboard */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-['Barlow_Condensed']">Team Leaderboard</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {(team_performance?.members || []).slice(0, 5).map((member, idx) => (
                <div key={member.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                      idx === 0 ? 'bg-amber-500' : idx === 1 ? 'bg-slate-400' : idx === 2 ? 'bg-amber-700' : 'bg-slate-300'
                    }`}>
                      {idx + 1}
                    </span>
                    <div>
                      <p className="font-medium text-slate-900">{member.name}</p>
                      <p className="text-xs text-slate-500 capitalize">{member.role}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-slate-900">{member.jobs_assigned} jobs</p>
                    <p className="text-xs text-slate-500">{member.hours_logged}h logged</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Section 6: Operational Efficiency */}
      <div className="space-y-4">
        <h2 className="text-xl font-bold text-slate-800 font-['Barlow_Condensed'] flex items-center gap-2">
          <Activity className="w-5 h-5 text-amber-500" />
          Operational Efficiency
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Calendar Utilization</p>
                <Calendar className="w-5 h-5 text-slate-400" />
              </div>
              <p className="text-3xl font-bold text-slate-900">{efficiency?.calendar_utilization || 0}%</p>
              <div className="mt-2 h-2 bg-slate-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-amber-500 rounded-full transition-all"
                  style={{ width: `${Math.min(efficiency?.calendar_utilization || 0, 100)}%` }}
                />
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Timesheet Compliance</p>
                <Clock className="w-5 h-5 text-slate-400" />
              </div>
              <p className="text-3xl font-bold text-slate-900">{efficiency?.timesheet_compliance || 0}%</p>
              <div className="mt-2 h-2 bg-slate-200 rounded-full overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all ${
                    (efficiency?.timesheet_compliance || 0) >= 80 ? 'bg-emerald-500' : 
                    (efficiency?.timesheet_compliance || 0) >= 50 ? 'bg-amber-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${Math.min(efficiency?.timesheet_compliance || 0, 100)}%` }}
                />
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold uppercase tracking-wider text-red-500">Overdue Jobs</p>
                <AlertCircle className="w-5 h-5 text-red-400" />
              </div>
              <p className="text-3xl font-bold text-red-600">{efficiency?.overdue_jobs || 0}</p>
              <p className="text-xs text-slate-500 mt-2">Scheduled but not completed</p>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold uppercase tracking-wider text-emerald-600">Completion Rate</p>
                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
              </div>
              <p className="text-3xl font-bold text-emerald-600">{job_performance?.completion_rate || 0}%</p>
              <div className="mt-2 h-2 bg-slate-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-emerald-500 rounded-full transition-all"
                  style={{ width: `${Math.min(job_performance?.completion_rate || 0, 100)}%` }}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
